if (!customElements.get("embla-carousel-pagination")) {
  class EmblaCarouselPagination extends HTMLElement {
    constructor() {
      super();
      this.emblaCarousel = null;
      this.dots = [];
      this.currentSlide = 0;
    }

    setEmblaCarousel(emblaCarousel) {
      this.emblaCarousel = emblaCarousel;
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.createPaginationDots();
        this.setupEmblaListeners();
      }, 50);
    }

    createPaginationDots() {
      if (!this.emblaCarousel || !this.emblaCarousel.emblaApi) {
        const carouselId = this.emblaCarousel?._debugCarouselId || 'unknown';
        return;
      }

      // Get carousel identifier
      const carouselId = this.emblaCarousel._debugCarouselId || 'unknown';
      
      // CRITICAL: Always use emblaApi.slideNodes().length because that's what Embla actually has
      // after initializeEmbla() was called with config.slides. The visibleSlides cache might be stale.
      // When initializeEmbla() is called with config.slides = [4 slides], emblaApi.slideNodes() 
      // will return only those 4 slides, regardless of what visibleSlides says.
      const allSlidesFromApi = this.emblaCarousel.emblaApi.slideNodes();
      
      // Filter to only include direct children of the slide container (not nested elements like <img> inside <a>)
      const slideContainer = this.emblaCarousel.slideContainer || 
        (this.emblaCarousel.viewport ? this.emblaCarousel.viewport.querySelector('.embla__container') : null);
      
      const directChildrenOnly = slideContainer 
        ? allSlidesFromApi.filter(slide => slide.parentElement === slideContainer)
        : allSlidesFromApi;
      
      // Check if carousel is within a product-card element
      const parentProductCard = this.emblaCarousel.closest('product-card');
      const parentVariantId = parentProductCard ? parentProductCard.getAttribute('data-variant-id') : null;
      
      // Filter slides based on variant ID matching if within product-card
      // Use directChildrenOnly instead of allSlidesFromApi to exclude nested elements
      let matchingSlides = directChildrenOnly;
      if (parentProductCard && parentVariantId) {
        // Only include slides whose data-variant-ids contains the parent's data-variant-id
        matchingSlides = directChildrenOnly.filter(slide => {
          const slideVariantIds = slide.getAttribute('data-variant-ids');
          if (!slideVariantIds) return false;
          // Parse comma-separated variant IDs
          const variantIds = slideVariantIds.split(',').map(id => id.trim());
          return variantIds.includes(parentVariantId);
        });
      }
      
      // After variant ID filtering, filter by actual visibility
      // Only create dots for slides that are both matching variant ID AND visible
      matchingSlides = matchingSlides.filter(slide => {
        // Check for hidden class
        if (slide.classList.contains('hidden')) {
          return false;
        }
        
        // Check computed display style
        const style = window.getComputedStyle(slide);
        if (style.display === 'none') {
          return false;
        }
        
        return true;
      });
      
      const totalSlides = matchingSlides.length;
      
      
      if (totalSlides <= 1) {
        // Hide pagination if only one slide
        this.innerHTML = "";
        this.dots = [];
        return;
      }

      // Clear existing dots
      this.innerHTML = "";
      this.dots = [];

      // Get pagination styling from data attributes
      const inactiveDotClasses = this.getPaginationDotClasses();
      const activeDotClasses = this.getPaginationActiveDotClasses();

      // Create dots based on matching slides
      // Map matching slides to their original indices in directChildrenOnly (which is what Embla should see)
      const slideIndexMap = new Map();
      matchingSlides.forEach(slide => {
        const originalIndex = directChildrenOnly.indexOf(slide);
        if (originalIndex !== -1) {
          slideIndexMap.set(slideIndexMap.size, originalIndex);
        }
      });

      // Create dots for matching slides
      for (let i = 0; i < totalSlides; i++) {
        const dot = document.createElement("button");
        
        // Apply only the inactive classes from data attributes (no hardcoded classes)
        if (inactiveDotClasses) {
          inactiveDotClasses.split(' ').forEach(cls => {
            if (cls.trim()) dot.classList.add(cls.trim());
          });
        }
        
        // Store the original slide index for scrolling
        const originalSlideIndex = slideIndexMap.get(i);
        dot.setAttribute("data-slide", originalSlideIndex);
        dot.setAttribute("aria-label", `Go to slide ${i + 1}`);

        // Add click handler - use original slide index
        dot.addEventListener("click", () => this.scrollToSlide(originalSlideIndex));

        this.appendChild(dot);
        this.dots.push(dot);
      }
      
      // Store active dot classes for later use
      this.activeDotClasses = activeDotClasses;
      this.inactiveDotClasses = inactiveDotClasses;
      this.updateActiveDot(0);
    }

    scrollToSlide(slideIndex) {
      if (!this.emblaCarousel || !this.emblaCarousel.emblaApi) {
        return;
      }
      
      this.emblaCarousel.emblaApi.scrollTo(slideIndex);
    }

    setupEmblaListeners() {
      if (!this.emblaCarousel || !this.emblaCarousel.emblaApi) {
        return;
      }

      // Listen to Embla select event
      this.emblaCarousel.emblaApi.on('select', () => {
        this.updateActiveDot(this.emblaCarousel.emblaApi.selectedScrollSnap());
      });

      // Listen to slidesChanged event to recalculate pagination when slides are added/removed
      this.emblaCarousel.emblaApi.on('slidesChanged', () => {
        // Store the currently selected slide index before recalculation
        const selectedSlideIndex = this.emblaCarousel.emblaApi.selectedScrollSnap();
        
        // Recalculate pagination dots
        this.createPaginationDots();
        
        // Find the dot index that corresponds to the selected slide
        // Each dot has a data-slide attribute with the original slide index
        let dotIndex = 0;
        for (let i = 0; i < this.dots.length; i++) {
          const dotSlideIndex = parseInt(this.dots[i].getAttribute('data-slide'));
          if (dotSlideIndex === selectedSlideIndex) {
            dotIndex = i;
            break;
          }
        }
        
        // Update active dot to match the selected slide
        this.updateActiveDot(dotIndex);
      });
    }

    updateActiveDot(slideIndex) {
      this.dots.forEach((dot, index) => {
        if (index === slideIndex) {
          // Remove inactive background classes
          if (this.inactiveDotClasses) {
            this.inactiveDotClasses.split(' ').forEach(cls => {
              if (cls.trim() && cls.includes('bg-')) dot.classList.remove(cls.trim());
            });
          }
          
          // Add active background classes
          if (this.activeDotClasses) {
            this.activeDotClasses.split(' ').forEach(cls => {
              if (cls.trim() && cls.includes('bg-')) dot.classList.add(cls.trim());
            });
          }
        } else {
          // Remove active background classes
          if (this.activeDotClasses) {
            this.activeDotClasses.split(' ').forEach(cls => {
              if (cls.trim() && cls.includes('bg-')) dot.classList.remove(cls.trim());
            });
          }
          
          // Add inactive background classes
          if (this.inactiveDotClasses) {
            this.inactiveDotClasses.split(' ').forEach(cls => {
              if (cls.trim() && cls.includes('bg-')) dot.classList.add(cls.trim());
            });
          }
        }
      });
      this.currentSlide = slideIndex;
    }

    getPaginationDotClasses() {
      return this.dataset.paginationDotClasses || '';
    }

    getPaginationActiveDotClasses() {
      return this.dataset.paginationActiveDotClasses || '';
    }

    getArrowClasses() {
      return this.dataset.arrowClasses || '';
    }

    getArrowSvg() {
      const encodedSvg = this.dataset.arrowSvg || '';
      if (!encodedSvg) return '';
      
      // Decode HTML entities by creating a temporary element
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = encodedSvg;
      return tempDiv.innerHTML;
    }

    getArrowSvgMarkup(direction) {
      const customSvg = this.getArrowSvg();
      
      if (customSvg) {
        // If custom SVG is provided, use it and add rotate-180 class for left arrow
        if (direction === 'prev') {
          return customSvg.replace('<svg', '<svg class="rotate-180"');
        }
        return customSvg;
      }
      
      // Fallback to default chevron
      const svgSize = '16';
      const svgViewBox = '0 0 16 16';
      const path = 'M6 4L10 8L6 12';
      
      let svgClass = '';
      if (direction === 'prev') {
        svgClass = ' class="rotate-180"';
      }
      
      return `<svg${svgClass} width="${svgSize}" height="${svgSize}" viewBox="${svgViewBox}" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="${path}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }

    disconnectedCallback() {
      // Cleanup handled by parent component
    }
  }

  customElements.define("embla-carousel-pagination", EmblaCarouselPagination);
}

if (!customElements.get("embla-carousel-controls")) {
  class EmblaCarouselControls extends HTMLElement {
    constructor() {
      super();
      this.emblaCarousel = null;
      this.prevButton = null;
      this.nextButton = null;
    }

    setEmblaCarousel(emblaCarousel) {
      this.emblaCarousel = emblaCarousel;
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.createControls();
        this.setupEmblaListeners();
      }, 50);
    }

    createControls() {
      if (!this.emblaCarousel) {
        return;
      }

      // Check if custom arrows are already provided
      if (this.hasAttribute('data-custom-arrows') && this.children.length > 0) {
        // Custom arrows are already rendered, just add event listeners
        this.setupCustomArrowListeners();
        return;
      }

      // Clear existing content
      this.innerHTML = "";

      // Get arrow styling from data attributes
      const arrowClasses = this.getArrowClasses();

      // Create previous button
      this.prevButton = document.createElement("button");
      this.prevButton.innerHTML = this.getArrowSvgMarkup('prev');
      
      // Apply base classes
      this.prevButton.classList.add(
        "z-10",
        "flex",
        "items-center",
        "justify-center",
        "transition-all",
        "duration-200",
        "disabled:opacity-50",
        "disabled:cursor-not-allowed"
      );

      if (!this.dataset.alternativeArrowPosition) {
        this.prevButton.classList.add(
          "absolute",
          "left-12",
          "md:left-8",
          "top-1/2",
          "-translate-y-1/2"
        );
      }
      
      // Apply custom classes from data attributes
      if (arrowClasses) {
        arrowClasses.split(' ').forEach(cls => {
          if (cls.trim()) this.prevButton.classList.add(cls.trim());
        });
      }
      
      this.prevButton.setAttribute("aria-label", "Previous slide");
      this.prevButton.addEventListener("click", () => this.scrollPrev());

      // Create next button
      this.nextButton = document.createElement("button");
      this.nextButton.innerHTML = this.getArrowSvgMarkup('next');
      
      // Apply base classes
      this.nextButton.classList.add(
        "z-10",
        "flex",
        "items-center",
        "justify-center",
        "duration-200",
        "disabled:opacity-50",
        "disabled:cursor-not-allowed"
      );
      if (!this.dataset.alternativeArrowPosition) {
        this.nextButton.classList.add(
          "absolute",
          "right-12",
          "md:right-8",
          "top-1/2",
          "-translate-y-1/2"
        );
      }
      
      // Apply custom classes from data attributes
      if (arrowClasses) {
        arrowClasses.split(' ').forEach(cls => {
          if (cls.trim()) this.nextButton.classList.add(cls.trim());
        });
      }
      
      this.nextButton.setAttribute("aria-label", "Next slide");
      this.nextButton.addEventListener("click", () => this.scrollNext());

      this.appendChild(this.prevButton);
      this.appendChild(this.nextButton);
    }

    scrollPrev() {
      if (!this.emblaCarousel || !this.emblaCarousel.emblaApi) {
        return;
      }
      
      this.emblaCarousel.emblaApi.scrollPrev();
    }

    scrollNext() {
      if (!this.emblaCarousel || !this.emblaCarousel.emblaApi) {
        return;
      }
      
      this.emblaCarousel.emblaApi.scrollNext();
    }

    setupCustomArrowListeners() {
      if (!this.emblaCarousel || !this.emblaCarousel.emblaApi) {
        return;
      }

      // Find custom arrow buttons
      const prevButton = this.querySelector('[data-direction="prev"], [data-embla-previous]');
      const nextButton = this.querySelector('[data-direction="next"], [data-embla-next]');

      if (prevButton) {
        prevButton.addEventListener("click", () => this.scrollPrev());
        this.prevButton = prevButton;
      }

      if (nextButton) {
        nextButton.addEventListener("click", () => this.scrollNext());
        this.nextButton = nextButton;
      }

      // Set up embla listeners for custom arrows
      this.setupEmblaListeners();
    }

    setupEmblaListeners() {
      if (!this.emblaCarousel || !this.emblaCarousel.emblaApi) {
        return;
      }

      // Listen to Embla select event to update button states
      this.emblaCarousel.emblaApi.on('select', () => {
        this.updateButtonStates();
      });
    }

    updateButtonStates() {
      if (!this.emblaCarousel || !this.emblaCarousel.emblaApi) {
        return;
      }

      const { canScrollPrev, canScrollNext } = this.emblaCarousel.emblaApi;
      
      // Handle custom arrows
      if (this.hasAttribute('data-custom-arrows')) {
        const prevButton = this.querySelector('[data-direction="prev"], [data-embla-previous]');
        const nextButton = this.querySelector('[data-direction="next"], [data-embla-next]');
        
        if (prevButton) {
          prevButton.disabled = !canScrollPrev();
        }
        if (nextButton) {
          nextButton.disabled = !canScrollNext();
        }
        return;
      }

      // Handle default arrows
      if (this.prevButton && this.nextButton) {
        this.prevButton.disabled = !canScrollPrev();
        this.nextButton.disabled = !canScrollNext();
      }
    }

    disconnectedCallback() {
      // Cleanup handled by parent component
    }

    getArrowClasses() {
      return this.dataset.arrowClasses || '';
    }

    getArrowSvg() {
      const encodedSvg = this.dataset.arrowSvg || '';
      if (!encodedSvg) return '';
      
      // Decode HTML entities by creating a temporary element
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = encodedSvg;
      return tempDiv.innerHTML;
    }

    getArrowSvgMarkup(direction) {
      const customSvg = this.getArrowSvg();
      
      if (customSvg) {
        // If custom SVG is provided, use it and add rotate-180 class for left arrow
        if (direction === 'prev') {
          return customSvg.replace('<svg', '<svg class="rotate-180"');
        }
        return customSvg;
      }
      
      // Fallback to default chevron
      const svgSize = '16';
      const svgViewBox = '0 0 16 16';
      const path = 'M6 4L10 8L6 12';
      
      let svgClass = '';
      if (direction === 'prev') {
        svgClass = ' class="rotate-180"';
      }
      
      return `<svg${svgClass} width="${svgSize}" height="${svgSize}" viewBox="${svgViewBox}" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="${path}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }
  }

  customElements.define("embla-carousel-controls", EmblaCarouselControls);
}

if (!customElements.get("embla-carousel")) {
  class EmblaCarousel extends HTMLElement {
    constructor() {
      super();
      this.emblaApi = null;
      this.autoplayPlugin = null;
      this.wheelGesturesPlugin = null;
      this.container = null;
      this.viewport = null;
      this.slides = [];
      this.resizeObserver = null;
      this.xlMediaQuery = null;
      this.pointerMediaQuery = null;
      
      // Default configuration values (overridden by data attributes in parseConfiguration)
      this.blocksToShow = 1;
      this.blocksToShowDesktop = 4;
      this.infiniteLoop = false;
      this.infiniteLoopDesktop = false;
      this.showArrows = false;
      this.showArrowsDesktop = false;
      this.autoPlay = false;
      this.autoPlayDesktop = false;
      this.autoPlayDelay = 3000;
      this.autoPlayDelayDesktop = 3000;
      this.pauseOnHover = true;
      this.pauseOnHoverDesktop = true;
      this.alignItems = 'center';
      this.alignItemsDesktop = 'default';
      this.align = 'center';
      this.alignDesktop = 'default';
      this.autoScroll = false;
      this.autoScrollDesktop = false;
      this.autoScrollSpeed = 2;
      this.autoScrollSpeedDesktop = 2;
      this.autoScrollDirection = 'forward';
      this.autoScrollDirectionDesktop = 'default';
      this.autoScrollPauseOnHover = true;
      this.autoScrollPauseOnHoverDesktop = true;
      this.intersectionObserver = null;
      this.isInitialized = false;
    }

    connectedCallback() {
      // Wait for Embla scripts to be fully loaded first
      if (!window.EmblaCarousel) {
        setTimeout(() => this.connectedCallback(), 100);
        return;
      }
      
      // Wait for page to be fully rendered before initializing
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          // Use requestAnimationFrame to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            this.setupLazyInitialization();
          });
        });
      } else {
        // Page already loaded, use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          this.setupLazyInitialization();
        });
      }
    }

    setupLazyInitialization() {
      // Prevent double initialization
      if (this.isInitialized) {
        return;
      }

      // Check if element is already visible or near viewport
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const threshold = viewportHeight * 0.5; // 50% of viewport
      
      const isVisible = rect.top < viewportHeight + threshold && rect.bottom > -threshold;
      
      if (isVisible) {
        // Initialize with a small delay to stagger initialization and prevent bottleneck
        // Use a shared queue to batch initializations
        EmblaCarousel.queueInitialization(this);
      } else {
        // Use Intersection Observer for lazy initialization
        if (!this.intersectionObserver) {
          this.intersectionObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  // Queue initialization instead of immediate init
                  EmblaCarousel.queueInitialization(entry.target);
                  // Unobserve after queuing (will be initialized soon)
                  if (this.intersectionObserver) {
                    this.intersectionObserver.unobserve(entry.target);
                  }
                }
              });
            },
            {
              root: null,
              rootMargin: '50% 0px 50% 0px', // 50% margin on top and bottom
              threshold: 0
            }
          );
        }
        this.intersectionObserver.observe(this);
      }
    }

    /**
     * Queue initialization to stagger and prevent bottleneck
     * Processes one carousel per frame
     */
    static queueInitialization(carousel) {
      if (!EmblaCarousel.initQueue) {
        EmblaCarousel.initQueue = [];
        EmblaCarousel.initProcessing = false;
      }

      // Add to queue if not already queued
      if (!carousel._queuedForInit) {
        carousel._queuedForInit = true;
        EmblaCarousel.initQueue.push(carousel);
      }

      // Start processing if not already running
      if (!EmblaCarousel.initProcessing) {
        EmblaCarousel.initProcessing = true;
        EmblaCarousel.processInitQueue();
      }
    }

    /**
     * Process initialization queue one carousel per frame
     */
    static processInitQueue() {
      if (EmblaCarousel.initQueue.length === 0) {
        EmblaCarousel.initProcessing = false;
        return;
      }

      // Process one carousel per frame
      const carousel = EmblaCarousel.initQueue.shift();
      if (carousel && !carousel.isInitialized) {
        carousel.initializeEmblaCarousel();
      }

      // Schedule next carousel for next frame
      requestAnimationFrame(() => {
        EmblaCarousel.processInitQueue();
      });
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      if (this.emblaApi) {
        this.emblaApi.destroy();
        this.emblaApi = null;
      }
      
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      
      if (this.xlMediaQuery) {
        this.xlMediaQuery.removeEventListener("change", this.handleBreakpointChange.bind(this));
      }
      
      if (this.pointerMediaQuery) {
        this.pointerMediaQuery.removeEventListener("change", this.handleBreakpointChange.bind(this));
      }
    }

    async initializeEmblaCarousel() {
      // Prevent double initialization
      if (this.isInitialized) {
        return;
      }
      this.isInitialized = true;

      // Load Embla Carousel and plugins
      try {
        // Wait for Embla to be available
        await this.waitForEmbla();
        
        // Parse configuration from data attributes
        this.parseConfiguration();
        
        // Find container and viewport
        this.findElements();
        
        if (!this.container || !this.viewport || !this.slideContainer) {
          console.warn("EmblaCarousel: container, viewport or slideContainer not found");
          return;
        }
        
        // Set up responsive handling
        this.setupResponsiveHandling();
        
        // Get ALL slides from the container (including hidden ones - they stay in DOM)
        const allChildren = Array.from(this.slideContainer.children);
        this.slides = allChildren; // Keep all elements in the array
        
        // Add embla__slide class to ALL slides (including hidden ones)
        // This allows us to use the CSS selector .embla__slide:not(.hidden) in config.slides
        this.slides.forEach(slide => {
          if (!slide.classList.contains('embla__slide')) {
            slide.classList.add('embla__slide');
          }
        });
        
        // Initialize Embla with current configuration
        this.initializeEmbla();
        
        // Convert container gap classes to per-slide mr classes for Embla compatibility
        this.applyGapClassesToSlides();
        
        // Initialize pagination and controls
        this.initializePagination();
        this.initializeControls();
        
        // Set initial visibility for pagination and arrows
        this.updatePaginationVisibility();
        this.updateArrowsVisibility();
        
        // Set up resize observer
        this.setupResizeObserver();
        
      } catch (error) {
        console.error("Failed to initialize Embla Carousel:", error);
      }
    }

    async waitForEmbla() {
      return new Promise((resolve) => {
        const checkEmbla = () => {
          if (window.EmblaCarousel) {
            resolve();
          } else {
            setTimeout(checkEmbla, 50);
          }
        };
        checkEmbla();
      });
    }

    parseConfiguration() {
      this.blocksToShow = this.dataset.blocksToShow ? parseInt(this.dataset.blocksToShow) : 1;
      this.blocksToShowDesktop = this.dataset.blocksToShowDesktop ? parseInt(this.dataset.blocksToShowDesktop) : 4;
      this.infiniteLoop = this.dataset.infiniteLoop === "true";
      this.infiniteLoopDesktop = this.dataset.infiniteLoopDesktop === "true";
      this.showArrows = this.dataset.showArrows === "true";
      this.showArrowsDesktop = this.dataset.showArrowsDesktop === "true";
      
      this.autoPlay = this.dataset.autoPlay === "true";
      this.autoPlayDesktop = this.dataset.autoPlayDesktop === "true";
      
      this.autoPlayDelay = this.dataset.autoPlayDelay ? parseInt(this.dataset.autoPlayDelay) : 3000;
      this.autoPlayDelayDesktop = this.dataset.autoPlayDelayDesktop ? parseInt(this.dataset.autoPlayDelayDesktop) : 3000;
      this.pauseOnHover = this.dataset.pauseOnHover !== "false";
      this.pauseOnHoverDesktop = this.dataset.pauseOnHoverDesktop !== "false";
      this.alignItems = this.dataset.alignItems || 'center';
      this.alignItemsDesktop = this.dataset.alignItemsDesktop || 'default';
      this.align = this.dataset.align || 'center';
      this.alignDesktop = this.dataset.alignDesktop || 'default';
      this.autoScrollSpeed = this.dataset.autoScrollSpeed ? parseInt(this.dataset.autoScrollSpeed) : 2;
      this.autoScrollSpeedDesktop = this.dataset.autoScrollSpeedDesktop ? parseInt(this.dataset.autoScrollSpeedDesktop) : 2;
      this.autoScrollDirection = this.dataset.autoScrollDirection || 'forward';
      this.autoScrollDirectionDesktop = this.dataset.autoScrollDirectionDesktop || 'default';
      this.autoScrollPauseOnHover = this.dataset.autoScrollPauseOnHover !== "false";
      this.autoScrollPauseOnHoverDesktop = this.dataset.autoScrollPauseOnHoverDesktop !== "false";
    }

    findElements() {
      // The container is the embla-carousel element itself (root .embla)
      this.container = this;
      
      // Find the viewport using Embla's expected class
      this.viewport = this.querySelector('.embla__viewport');
      
      if (!this.viewport) {
        console.warn("EmblaCarousel: No .embla__viewport element found");
        return;
      }
      
      // Find the container element (holds the slides)
      this.slideContainer = this.viewport.querySelector('.embla__container');
      
      if (!this.slideContainer) {
        console.warn("EmblaCarousel: No .embla__container element found");
        return;
      }
    }

    setupResponsiveHandling() {
      // Set up media query listeners
      this.xlMediaQuery = window.matchMedia("(min-width: 1280px)");
      this.pointerMediaQuery = window.matchMedia("(pointer: coarse)");
      
      // Initial call
      this.handleBreakpointChange();
      
      // Listen for changes
      this.xlMediaQuery.addEventListener("change", this.handleBreakpointChange.bind(this));
      this.pointerMediaQuery.addEventListener("change", this.handleBreakpointChange.bind(this));
    }

    handleBreakpointChange() {
      if (!this.emblaApi) {
        return;
      }
      
      const isDesktop = this.xlMediaQuery.matches;
      const isCoarsePointer = this.pointerMediaQuery.matches;
      
      // Get current configuration based on breakpoint
      const currentConfig = this.getCurrentConfiguration(isDesktop, isCoarsePointer);
      
      // Get direct children only (not nested elements like <img> inside <a>)
      // Filter to only include direct children of slideContainer that match the selector
      const allDirectChildren = Array.from(this.slideContainer.children);
      const visibleDirectChildren = allDirectChildren.filter(slide => {
        // Must have embla__slide class
        if (!slide.classList.contains('embla__slide')) return false;
        // Must not be hidden
        if (slide.classList.contains('hidden')) return false;
        // Check computed display style
        const style = window.getComputedStyle(slide);
        if (style.display === 'none') return false;
        return true;
      });
      
      // Pass array of direct children to Embla (prevents nested elements from being counted)
      currentConfig.slides = visibleDirectChildren;
      
      // Reinitialize Embla with new configuration
      this.emblaApi.reInit(currentConfig);

      // Re-apply class conversion after re-init as breakpoints/options may change
      this.applyGapClassesToSlides();

      // Re-render pagination dots to reflect new slide count after visibility changes
      if (this.pagination && typeof this.pagination.createPaginationDots === 'function') {
        this.pagination.createPaginationDots();
      }

      // Update pagination and arrows visibility based on current breakpoint
      this.updatePaginationVisibility();
      this.updateArrowsVisibility();
    }

    getCurrentConfiguration(isDesktop, isCoarsePointer) {
      // Get align value for Embla slide alignment (separate from flex item alignment)
      let alignValue = isDesktop ? this.alignDesktop : this.align;
      
      // Handle 'default' value - fallback to center
      if (alignValue === 'default') {
        alignValue = 'center';
      }
      
      // Validate align value (should be 'start', 'center', or 'end')
      if (!['start', 'center', 'end'].includes(alignValue)) {
        alignValue = 'center'; // Fallback to center if invalid
      }
      
      const config = {
        align: alignValue,
        loop: isDesktop ? this.infiniteLoopDesktop : this.infiniteLoop,
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
        dragFree: false,
        watchDrag: true,
        watchResize: true,
        watchSlides: true,
        watchFocus: true,
        inViewThreshold: 0,
        skipSnaps: false,
        startIndex: 0,
        axis: 'x',
        direction: 'ltr',
        dragThreshold: 10
      };
      
      // Handle blocks to show (slides per view)
      const blocksToShow = isDesktop ? this.blocksToShowDesktop : this.blocksToShow;
      const isLoopEnabled = isDesktop ? this.infiniteLoopDesktop : this.infiniteLoop;
      
      if (blocksToShow === 0) {
        // Natural sizing - let slides use their natural width
        config.slidesToScroll = 'auto';
        // When using loop with variable widths, disable containScroll
        if (isLoopEnabled) {
          config.containScroll = false;
        } else {
          config.containScroll = 'trimSnaps';
        }
      } else if (blocksToShow > 1) {
        // Show multiple slides per view
        config.slidesToScroll = blocksToShow;
      }
      
      // Initialize plugins array
      this.plugins = [];
      
      // Add autoplay if enabled
      if ((isDesktop ? this.autoPlayDesktop : this.autoPlay) && window.EmblaCarouselAutoplay) {
        const autoplayDelay = isDesktop ? this.autoPlayDelayDesktop : this.autoPlayDelay;
        const pauseOnHover = isDesktop ? this.pauseOnHoverDesktop : this.pauseOnHover;
        
        this.autoplayPlugin = window.EmblaCarouselAutoplay({
          delay: autoplayDelay,
          stopOnInteraction: pauseOnHover,
          stopOnMouseEnter: pauseOnHover,
          stopOnFocusIn: true,
          stopOnLastSnap: false,
          playOnInit: true
        });
        
        this.plugins.push(this.autoplayPlugin);
      }
      
      // Add auto-scroll if enabled (incompatible with autoplay, so only use if autoplay is disabled)
      const autoScrollEnabled = isDesktop ? this.autoScrollDesktop : this.autoScroll;
      const autoPlayEnabled = isDesktop ? this.autoPlayDesktop : this.autoPlay;
      
      if (autoScrollEnabled && !autoPlayEnabled && window.EmblaCarouselAutoScroll) {
        const autoScrollSpeed = isDesktop ? this.autoScrollSpeedDesktop : this.autoScrollSpeed;
        let autoScrollDirection = isDesktop ? this.autoScrollDirectionDesktop : this.autoScrollDirection;
        
        // Handle "default" for desktop
        if (autoScrollDirection === 'default' && isDesktop) {
          autoScrollDirection = this.autoScrollDirection;
        }
        
        // Use autoScrollPauseOnHover if explicitly set (backward compatibility), otherwise fall back to pauseOnHover
        const autoScrollPauseOnHover = isDesktop ? this.autoScrollPauseOnHoverDesktop : this.autoScrollPauseOnHover;
        const pauseOnHover = isDesktop ? this.pauseOnHoverDesktop : this.pauseOnHover;
        
        // Check if old auto-scroll-pause-on-hover attribute exists (backward compatibility)
        const autoScrollAttrName = isDesktop ? 'data-auto-scroll-pause-on-hover-desktop' : 'data-auto-scroll-pause-on-hover';
        const hasAutoScrollPauseAttr = this.hasAttribute(autoScrollAttrName);
        const usePauseOnHover = hasAutoScrollPauseAttr ? autoScrollPauseOnHover : pauseOnHover;
        
        this.autoScrollPlugin = window.EmblaCarouselAutoScroll({
          speed: autoScrollSpeed,
          direction: autoScrollDirection,
          startDelay: 1000,
          playOnInit: true,
          stopOnInteraction: true,
          stopOnMouseEnter: usePauseOnHover,
          stopOnFocusIn: true
        });
        
        this.plugins.push(this.autoScrollPlugin);
      }
      
      // Add wheel gestures if available and not on touch device
      if (!isCoarsePointer && window.EmblaCarouselWheelGestures) {
        this.wheelGesturesPlugin = window.EmblaCarouselWheelGestures();
        this.plugins.push(this.wheelGesturesPlugin);
      }
      
      return config;
    }

    initializeEmbla() {
      const isDesktop = this.xlMediaQuery ? this.xlMediaQuery.matches : window.innerWidth >= 1280;
      const isCoarsePointer = this.pointerMediaQuery ? this.pointerMediaQuery.matches : false;
      const config = this.getCurrentConfiguration(isDesktop, isCoarsePointer);

      // Get carousel identifier
      const carouselId = this._debugCarouselId || 'unknown';

      // Get direct children only (not nested elements like <img> inside <a>)
      // Filter to only include direct children of slideContainer that match the selector
      const allDirectChildren = Array.from(this.slideContainer.children);
      const visibleDirectChildren = allDirectChildren.filter(slide => {
        // Must have embla__slide class
        if (!slide.classList.contains('embla__slide')) return false;
        // Must not be hidden
        if (slide.classList.contains('hidden')) return false;
        // Check computed display style
        const style = window.getComputedStyle(slide);
        if (style.display === 'none') return false;
        return true;
      });
      
      // Pass array of direct children to Embla (prevents nested elements from being counted)
      config.slides = visibleDirectChildren;

      // Initialize Embla Carousel with plugins as third parameter
      if (this.plugins && this.plugins.length > 0) {
        this.emblaApi = window.EmblaCarousel(this.viewport, config, this.plugins);
      } else {
        this.emblaApi = window.EmblaCarousel(this.viewport, config);
      }
      
      // Store plugin references for cleanup
      this.currentPlugins = this.plugins || [];
      
      // Check if loop was disabled by Embla
      const actualOptions = this.emblaApi.internalEngine().options;
      if (config.loop && !actualOptions.loop) {
        console.warn('Embla Carousel: Loop was disabled. Not enough slides to create loop effect.');
      }
      
      // Set up CSS for variable widths if needed
      this.setupVariableWidths();

      // Ensure mr classes reflect current gap classes
      this.applyGapClassesToSlides();
    }

    setupVariableWidths() {
      if (!this.slideContainer || !this.slides.length) {
        return;
      }
      
      const isDesktop = this.xlMediaQuery ? this.xlMediaQuery.matches : window.innerWidth >= 1280;
      const blocksToShow = isDesktop ? this.blocksToShowDesktop : this.blocksToShow;
      if (blocksToShow === 0) {
        // Natural sizing - slides use their natural width (already set by CSS in Liquid)
        // No need to override, the embla__slide class should handle this
      } else {
        // Fixed sizing based on blocks to show
        const slideWidth = `${100 / blocksToShow}%`;
        
        this.slides.forEach(slide => {
          slide.style.flex = `0 0 ${slideWidth}`;
          slide.style.minWidth = slideWidth;
        });
      }
    }

    initializePagination() {
      const paginationElement = this.querySelector("embla-carousel-pagination");
      if (paginationElement && paginationElement.setEmblaCarousel) {
        paginationElement.setEmblaCarousel(this);
      }
    }

    initializeControls() {
      const controlsElement = this.querySelector("embla-carousel-controls");
      if (controlsElement && controlsElement.setEmblaCarousel) {
        controlsElement.setEmblaCarousel(this);
      }
    }

    // Dynamically show/hide pagination based on settings
    updatePaginationVisibility() {
      const paginationElement = this.querySelector("embla-carousel-pagination");
      if (!paginationElement) return;

      // Check if pagination should be visible based on Liquid block settings
      const isDesktop = this.xlMediaQuery ? this.xlMediaQuery.matches : window.innerWidth >= 1280;
      
      // Check if pagination is hidden by Liquid block settings
      const isHiddenByLiquid = paginationElement.classList.contains("max-md:hidden") && !isDesktop;
      const isHiddenByLiquidDesktop = paginationElement.classList.contains("md:hidden") && isDesktop;
      
      // Show pagination if not hidden by Liquid settings and there are multiple slides
      const hasMultipleSlides = this.emblaApi && this.emblaApi.slideNodes().length > 1;
      const shouldShow = hasMultipleSlides && !isHiddenByLiquid && !isHiddenByLiquidDesktop;
      
      if (shouldShow) {
        paginationElement.classList.remove("hidden");
        paginationElement.classList.add("flex");
      } else {
        paginationElement.classList.remove("flex");
        paginationElement.classList.add("hidden");
      }
    }

    // Dynamically show/hide arrows based on settings
    updateArrowsVisibility() {
      const arrowsElement = this.querySelector("embla-carousel-controls");
      if (!arrowsElement) return;

      // Check if arrows should be visible based on Liquid block settings
      const isDesktop = this.xlMediaQuery ? this.xlMediaQuery.matches : window.innerWidth >= 1280;
      
      // Check if arrows are hidden by Liquid block settings
      const isHiddenByLiquid = arrowsElement.classList.contains("max-md:hidden") && !isDesktop;
      const isHiddenByLiquidDesktop = arrowsElement.classList.contains("md:hidden") && isDesktop;
      
      // Show arrows if not hidden by Liquid settings and there are multiple slides
      const hasMultipleSlides = this.emblaApi && this.emblaApi.slideNodes().length > 1;
      const shouldShow = hasMultipleSlides && !isHiddenByLiquid && !isHiddenByLiquidDesktop;
      
      if (shouldShow) {
        arrowsElement.classList.remove("hidden");
        arrowsElement.classList.add("flex");
      } else {
        arrowsElement.classList.remove("flex");
        arrowsElement.classList.add("hidden");
      }
    }

    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.emblaApi) {
          // Recalculate slide widths on resize
          this.setupVariableWidths();
          
          // Re-filter visible slides before reinit (but keep all in DOM)
          // Only get direct children (not nested elements like <img> inside <a>)
          const allChildren = Array.from(this.slideContainer.children);
          this.slides = allChildren; // Keep all elements in the array
          const visibleSlides = allChildren.filter(slide => {
            // Must have embla__slide class
            if (!slide.classList.contains('embla__slide')) return false;
            // Check for hidden class first (faster)
            if (slide.classList.contains('hidden')) {
              return false;
            }
            // Fallback: check computed style for display:none
            const style = window.getComputedStyle(slide);
            return style.display !== 'none';
          });
          this.visibleSlides = visibleSlides;
          
          // Update config with visible slides
          const config = this.emblaApi.internalEngine().options;
          if (visibleSlides.length > 0) {
            config.slides = visibleSlides;
          }
          
          this.emblaApi.reInit(config);
          this.applyGapClassesToSlides();
        }
      });
      
      this.resizeObserver.observe(this.slideContainer);
    }

    // Convert container gap classes (gap-*, md:gap-*) to mr classes on slides (mr-*, md:mr-*)
    getAllSlideNodes() {
      // Return all slides (including hidden ones) so gap classes apply to all
      // Embla only uses visible slides, but we want to style all slides
      return this.slides || [];
    }

    applyGapClassesToSlides() {
      if (!this.slideContainer) return;

      const container = this.slideContainer;
      const classTokens = Array.from(container.classList);

      // Match gap tokens we want to convert: gap-*, gap-x-*, md:gap-*, md:gap-x-*
      const gapTokenRegex = /^(?:md:)?gap-(?:x-)?[\w\[\]().:%/-]+$/;
      const gapTokens = classTokens.filter((t) => gapTokenRegex.test(t));

      if (!gapTokens.length) {
        return;
      }

      // Remove gap tokens from container
      gapTokens.forEach((t) => container.classList.remove(t));

      // Build mr tokens
      const toMr = (t) => {
        if (t.startsWith('md:')) {
          const inner = t.slice(3);
          return 'md:' + inner.replace(/^gap-x-/, 'mr-').replace(/^gap-/, 'mr-');
        }
        return t.replace(/^gap-x-/, 'mr-').replace(/^gap-/, 'mr-');
      };
      const mrTokens = gapTokens.map(toMr);

      // Idempotently apply mr tokens to slides
      const slides = this.getAllSlideNodes();
      slides.forEach((slide) => {
        mrTokens.forEach((tok) => {
          slide.classList.remove(tok);
          slide.classList.add(tok);
        });
      });
    }
  }

  // Wait for Embla to be ready before defining custom elements
  if (window.EmblaCarousel) {
    customElements.define("embla-carousel", EmblaCarousel);
  } else {
    console.log('Embla Carousel not ready for custom element registration, waiting...');
    setTimeout(() => {
      if (window.EmblaCarousel) {
        customElements.define("embla-carousel", EmblaCarousel);
      } else {
        console.error('Embla Carousel still not ready after timeout');
      }
    }, 100);
  }
}
